// src/features/editor/components/studio/useEditorCourseGate.ts
import { useCallback, useEffect, useRef, useState } from 'react'
import { useGetEditorCourseQuery } from '@/features/editor/coursesApi'
import { useFreshCourseModeration } from '@/features/moderation/useFreshCourseModeration'
import { expectedReadIdFrom } from '@/features/moderation/lib/expectedReadId'

/**
 * Что показать до рабочей области редактора. Курс открывается только СВЕЖИМ: по ответу
 * `/moderation`, полученному после открытия (`useFreshCourseModeration`), и прочитанным
 * из источника, который этот ответ назвал (`readId === draftId ?? liveId`). Курс из кеша
 * прошлого открытия мог быть прочитан из живой версии, а черновик с тех пор появился.
 *
 * Выход из расхождения источника держит САМ гейт, а не только поллер: поллер
 * перечитывает курс лишь на СМЕНЕ `draftId`, а её может не быть — кеш модерации
 * вытеснила инвалидация тега `Moderation` (`409 review_in_progress`), и первый же ответ
 * уже несёт черновик. Упавшее перечитывание при устаревшем кеше — экран ошибки с
 * «Повторить», а не вечный спиннер. После первого открытия смена источника рабочую
 * область не закрывает: черновики блоков пережили бы её зря.
 */
export const useEditorCourseGate = (courseId: number) => {
  const moderation = useFreshCourseModeration(courseId)
  const modState = moderation.state
  // `currentData`, а не `data`: на `/editor/A → /editor/B` `data` ещё держит курс A.
  const query = useGetEditorCourseQuery(courseId, { skip: modState === undefined })
  const { currentData: course, isError, isFetching, refetch } = query

  const [openedFor, setOpenedFor] = useState<number | null>(null)
  const opened = openedFor === courseId
  const expectedReadId = modState ? expectedReadIdFrom(modState, courseId) : undefined
  const canOpen = course !== undefined && (opened || course.readId === expectedReadId)
  if (canOpen && !opened) setOpenedFor(courseId)

  const staleSource = course !== undefined && !canOpen
  // Сам гейт перечитывает курс ОДИН раз на пару (курс, ожидаемый источник): если и после
  // этого источник не тот, автоматических запросов больше нет — только «Повторить».
  // Без счётчика успешный ответ с тем же чужим `readId` гасил бы `isFetching`, эффект
  // перезапускался бы, и запросы шли бы подряд без конца под вечным спиннером.
  const sourceKey = `${courseId}:${expectedReadId}`
  const selfRefetchedFor = useRef<string | null>(null)
  const [exhaustedFor, setExhaustedFor] = useState<string | null>(null)
  useEffect(() => {
    if (!staleSource || isFetching || isError) return
    if (selfRefetchedFor.current === sourceKey) {
      setExhaustedFor(sourceKey)
      return
    }
    selfRefetchedFor.current = sourceKey
    // Эффект родителя идёт после эффектов поллера: если тот уже перечитал курс, этот
    // `refetch` склеится с летящим запросом, а не пошлёт второй.
    refetch()
  }, [staleSource, isFetching, isError, refetch, sourceKey])

  // `refetch` зовётся только при `course !== undefined`: пока запрос курса пропущен
  // (`skip` — модерация ещё не ответила), RTK бросает на `refetch` незапущенного запроса.
  const retry = useCallback(() => {
    if (moderation.isError) moderation.refetch()
    if (course === undefined) return
    setExhaustedFor(null)
    refetch()
  }, [moderation, course, refetch])

  const firstLoadFailed = moderation.isError && modState === undefined
  return {
    course: canOpen ? course : undefined,
    draftOfId: course?.draftOfId,
    // Упавшее ПЕРЕчитывание уже открытого курса не выгоняет из редактора.
    courseMissing: isError && course === undefined,
    loadFailed: firstLoadFailed || (staleSource && (isError || exhaustedFor === sourceKey)),
    retry,
  }
}
